<script lang="ts">
  import type { Job } from '$lib/careers/types';
  import { ResponsiveVars, TwistBox, TwistGroup } from '@twist/core';
  import { cx } from '@emotion/css';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import { getIconByShape } from '$components/News/ButtonShapes';
  import JobLocation from '../[id]/components/JobLocation.svelte';
  import BlockButton from '$components/Button/BlockButton.svelte';
  import HorizontalBuLogo from '$components/Logos/HorizontalBULogo.svelte';
  import HeadingSizeDecorator from '$components/Common/HeadingSizeDecorator.svelte';
  import { flagValue } from '$lib/context/flags';
  import { FEATURE_FLAGS } from '$lib/flags/constants';

  export let job: Job;

  const responsiveVars = new ResponsiveVars()
    .addFluid('job-title-font-size', toFluidVarInput(24, 24))
    .addFluid('job-details-spacing', toFluidVarInput(30, 30))
    .addFluid('bu-img-container-width', toFluidVarInput(0, 150))
    .addFluid('bu-img-height', toFluidVarInput(24, 24))
    .addFluid('button-size', toFluidVarInput(24, 36))
    .toCSS();

  $: brandReskinEnabled = flagValue(FEATURE_FLAGS.BRAND_RESKIN);
</script>

<TwistGroup let:active>
  <a class={cx(responsiveVars, 'job')} href={`/careers/jobs/${job.id}`}>
    <TwistBox display={{ min: 'block', lg: 'none' }}>
      <div class="job-bu-img">
        <HorizontalBuLogo businessUnit={job.bu} />
      </div>
    </TwistBox>

    {#if brandReskinEnabled}
      <HeadingSizeDecorator size="h6">
        {job.title}
      </HeadingSizeDecorator>
    {:else}
      <p class="job-title">{job.title}</p>
    {/if}

    <div class="job-footer">
      <div class="job-info">
        <div class="job-bu-container-desktop">
          <div class="job-bu-img">
            <HorizontalBuLogo businessUnit={job.bu} />
          </div>
        </div>

        <JobLocation isRemote={job.isRemote} location={job.location} />
      </div>

      <BlockButton groupActive={active} motion="never">
        <svelte:component this={getIconByShape('circle')} slot="icon" />
      </BlockButton>
    </div>

    <div class="job-divider" />
  </a>
</TwistGroup>

<style lang="scss">
  @import 'src/scss/media';

  .job {
    --inner-button-background: black;
    --inner-button-foreground: white;

    color: unset;
    text-decoration: none;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--job-details-spacing);
    transition: opacity var(--twist-duration-100) var(--twist-ease-in-out-2);

    &:focus-visible {
      outline: 1px solid black;

      .job-divider {
        border-color: transparent;
      }
    }

    &:hover {
      --inner-button-background: white;
      --inner-button-foreground: black;

      .job-title {
        color: #666666;
      }
    }

    &:active {
      opacity: 0.6;

      --inner-button-background: black;
      --inner-button-foreground: white;

      .job-title {
        text-decoration: none;
      }
    }
  }

  .job-title {
    font-weight: 700;
    font-family: var(--font-family-primary);
    font-size: var(--job-title-font-size);
    line-height: 120%;
  }

  .job-footer {
    --theme-button-primary-button-default-foreground-color: var(--twist-color-gray-100);
    --theme-button-primary-button-active-foreground-color: var(--twist-color-gray-0);
    --theme-button-primary-button-hover-focus-foreground-color: var(--twist-color-gray-0);
    --theme-button-primary-button-default-background-color: #e2e2e2;
    --theme-button-primary-button-active-background-color: #666666;
    --theme-button-primary-button-hover-focus-background-color: #666666;
    flex: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .job-info {
    display: flex;
    align-items: center;
  }

  .job-bu-container-desktop {
    display: none;

    @include at('lg') {
      display: block;
      width: var(--bu-img-container-width);
    }
  }

  .job-bu-img {
    height: var(--bu-img-height);
    padding-right: var(--bu-img-pr);
  }

  .job-divider {
    border-top: 1px solid black;
    width: 100%;
    height: 1px;
  }
</style>
